import { z } from 'zod'

/**
 * Duplicate clients and people, and the rules a merge keeps. Decision 113.
 *
 * The three normalisers are the TypeScript twins of `fieldops_norm_email`,
 * `fieldops_norm_phone` and `fieldops_norm_name` in the database. The finder
 * runs on the database's (it compares every client with every other one);
 * these run where the set is small — the people on one client — and on the
 * screen, to say why two records were matched. `client-merge.test.ts` runs
 * both over the same cases, because a drift between them fails silently.
 */

export const MATCH_KEYS = ['EMAIL', 'PHONE', 'NAME'] as const
export type MatchKey = (typeof MATCH_KEYS)[number]

export const MATCH_LABEL: Record<MatchKey, string> = {
  EMAIL: 'email',
  PHONE: 'phone',
  NAME: 'name',
}

/**
 * How the finder decides two clients are the same.
 *
 * `ANY` groups clients that share any one of the chosen keys; `ALL` only those
 * that share every one. Email or phone, any, is the default: either is
 * evidence a name alone is not, because Australia has a lot of John Smiths.
 */
export const duplicateSearchSchema = z.object({
  match: z
    .array(z.enum(MATCH_KEYS))
    .min(1)
    .catch(['EMAIL', 'PHONE'])
    .default(['EMAIL', 'PHONE']),
  mode: z.enum(['ANY', 'ALL']).catch('ANY').default('ANY'),
  page: z.coerce.number().int().min(1).catch(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).catch(25).default(25),
})
export type DuplicateSearch = z.infer<typeof duplicateSearchSchema>

/**
 * A value shared by more clients than this is not evidence of anything: an
 * agency's inbox on every landlord it manages, or the office's own number
 * typed onto records that had none. The finder skips it and says so.
 */
export const COMMON_KEY_LIMIT = 10

export function normaliseEmail(value: string | null | undefined): string | null {
  const v = (value ?? '').replace(/\s/g, '').toLowerCase()
  return /^.+@.+$/.test(v) ? v : null
}

/**
 * Digits only, read as an Australian number. +61 and 0061 become 0, a mobile
 * typed without its 0 gets it back, and eight digits is a South Australian
 * landline typed without 08 — the business works in SA only.
 */
export function normalisePhone(value: string | null | undefined): string | null {
  const d = (value ?? '').replace(/[^0-9]/g, '')
  if (/^0061[0-9]{9}$/.test(d)) return `0${d.slice(4)}`
  if (/^61[0-9]{9}$/.test(d)) return `0${d.slice(2)}`
  if (/^4[0-9]{8}$/.test(d)) return `0${d}`
  if (/^[0-9]{8}$/.test(d)) return `08${d}`
  return d.length >= 8 ? d : null
}

const IGNORED_NAME_WORDS = new Set(['the', 'mr', 'mrs', 'ms', 'miss', 'dr', 'pty', 'ltd', 'limited', 'inc'])

/**
 * Words, lower case, in alphabetical order, without titles or a company's
 * legal suffix: "Smith, John", "Mr John Smith" and "john smith" are one name.
 */
export function normaliseName(value: string | null | undefined): string | null {
  const words = (value ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter((word) => word !== '' && !IGNORED_NAME_WORDS.has(word))
    .sort()
  return words.length > 0 ? words.join(' ') : null
}

/** A normalised phone, spaced the way it is said: 0491 570 156, 08 8555 1234. */
export function formatPhoneKey(key: string): string {
  if (/^04[0-9]{8}$/.test(key)) return `${key.slice(0, 4)} ${key.slice(4, 7)} ${key.slice(7)}`
  if (/^0[2378][0-9]{8}$/.test(key)) return `${key.slice(0, 2)} ${key.slice(2, 6)} ${key.slice(6)}`
  return key
}

/* -------------------------------------------------------------------------- */

/** Connected groups from pairs, each group's members in first-seen order. */
export function groupPairs(pairs: ReadonlyArray<readonly [string, string]>): string[][] {
  const parent = new Map<string, string>()
  const order: string[] = []

  const find = (id: string): string => {
    let root = id
    while (parent.get(root) !== root) root = parent.get(root)!
    // Path compression, so a long chain does not cost a walk per lookup.
    let node = id
    while (parent.get(node) !== root) {
      const next = parent.get(node)!
      parent.set(node, root)
      node = next
    }
    return root
  }

  for (const [a, b] of pairs) {
    for (const id of [a, b]) {
      if (!parent.has(id)) {
        parent.set(id, id)
        order.push(id)
      }
    }
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent.set(rb, ra)
  }

  const groups = new Map<string, string[]>()
  for (const id of order) {
    const root = find(id)
    const group = groups.get(root)
    if (group) group.push(id)
    else groups.set(root, [id])
  }
  return [...groups.values()]
}

/* -------------------------------------------------------------------------- */

export interface PersonLike {
  id: string
  firstName: string
  lastName?: string | null
  email?: string | null
  phone?: string | null
  mobile?: string | null
}

export interface PeopleMatch {
  ids: string[]
  /** What they share. An email or a number is enough to combine by default; a name alone is offered, not ticked. */
  reasons: MatchKey[]
  strong: boolean
}

/**
 * The people on one client who look like the same person.
 *
 * Two contacts match on an email, on any of their numbers against any of the
 * other's (a mobile typed into the phone box is still the same mobile), or on
 * the whole name. Run over the people of every client being merged, it is what
 * the merge window ticks under People; run over one client, it is the hint on
 * the client's page.
 */
export function matchPeople(people: ReadonlyArray<PersonLike>): PeopleMatch[] {
  const keyed = people.map((person) => ({
    id: person.id,
    email: normaliseEmail(person.email),
    phones: new Set(
      [normalisePhone(person.mobile), normalisePhone(person.phone)].filter((v): v is string => v !== null),
    ),
    name: normaliseName([person.firstName, person.lastName].filter(Boolean).join(' ')),
  }))

  const pairs: Array<[string, string]> = []
  const reasonsByPair = new Map<string, Set<MatchKey>>()

  for (let i = 0; i < keyed.length; i += 1) {
    for (let j = i + 1; j < keyed.length; j += 1) {
      const a = keyed[i]!
      const b = keyed[j]!
      const reasons = new Set<MatchKey>()
      if (a.email && a.email === b.email) reasons.add('EMAIL')
      if ([...a.phones].some((phone) => b.phones.has(phone))) reasons.add('PHONE')
      if (a.name && a.name === b.name) reasons.add('NAME')
      if (reasons.size > 0) {
        pairs.push([a.id, b.id])
        reasonsByPair.set(`${a.id}|${b.id}`, reasons)
      }
    }
  }

  return groupPairs(pairs).map((ids) => {
    const reasons = new Set<MatchKey>()
    for (const [key, found] of reasonsByPair) {
      const [a, b] = key.split('|')
      if (ids.includes(a!) && ids.includes(b!)) for (const reason of found) reasons.add(reason)
    }
    const list = MATCH_KEYS.filter((key) => reasons.has(key))
    return { ids, reasons: list, strong: reasons.has('EMAIL') || reasons.has('PHONE') }
  })
}

export interface CombinedPerson {
  lastName: string | null
  position: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  /** Details another record had that the kept person has no room for. */
  dropped: string[]
}

/**
 * Which email and numbers a combined person keeps, where the office chose. Each
 * is a value one of the people being combined already holds — a number may go
 * in either box — and a box left out is filled the usual way.
 */
export const personPicksSchema = z.object({
  email: z.string().trim().min(1).optional(),
  mobile: z.string().trim().min(1).optional(),
  phone: z.string().trim().min(1).optional(),
})
export type PersonPicks = z.infer<typeof personPicksSchema>

const emailKey = (value: string) => normaliseEmail(value) ?? value.toLowerCase()
const phoneKey = (value: string) => normalisePhone(value) ?? value

/**
 * The person that comes out of folding `others` into `kept`.
 *
 * The kept record's own details stay. An empty box takes the first value
 * another record has, and a number moves between mobile and phone rather than
 * being lost — but a person has one email, one mobile and one phone, so what
 * still has nowhere to go is named in `dropped` and shown before the merge.
 * `picks` overrides a box with a value from any of them, and whatever it
 * pushes out is dropped the same way.
 */
export function combinePeople(
  kept: PersonLike & { position?: string | null },
  others: ReadonlyArray<PersonLike & { position?: string | null }>,
  picks: PersonPicks = {},
): CombinedPerson {
  let lastName = kept.lastName?.trim() || null
  let position = kept.position?.trim() || null
  let email = kept.email?.trim() || null
  let mobile = kept.mobile?.trim() || null
  let phone = kept.phone?.trim() || null

  const haveNumber = (value: string) =>
    [mobile, phone].some((held) => held && phoneKey(held) === phoneKey(value))

  for (const other of others) {
    if (!lastName && other.lastName?.trim()) lastName = other.lastName.trim()
    if (!position && other.position?.trim()) position = other.position.trim()

    const otherEmail = other.email?.trim()
    if (otherEmail && !email) email = otherEmail

    for (const number of [other.mobile?.trim(), other.phone?.trim()]) {
      if (!number || haveNumber(number)) continue
      const isMobile = normalisePhone(number)?.startsWith('04') ?? false
      if (isMobile && !mobile) mobile = number
      else if (!phone) phone = number
      else if (!mobile) mobile = number
    }
  }

  if (picks.email) email = picks.email
  if (picks.mobile) mobile = picks.mobile
  if (picks.phone) phone = picks.phone
  if (picks.mobile || picks.phone) {
    // A number picked into one box leaves the other, rather than sit in both.
    if (mobile && phone && phoneKey(mobile) === phoneKey(phone)) {
      if (picks.phone && !picks.mobile) mobile = null
      else phone = null
    }
    // A box the pick left empty takes the next number nobody kept: a mobile
    // for the mobile box and a landline for the phone, before anything else.
    const spare = (wantMobile: boolean) => {
      const numbers = [kept, ...others]
        .flatMap((person) => [person.mobile?.trim(), person.phone?.trim()])
        .filter((number): number is string => Boolean(number) && !haveNumber(number!))
      return numbers.find((number) => phoneKey(number).startsWith('04') === wantMobile) ?? numbers[0] ?? null
    }
    if (!mobile && !picks.mobile) mobile = spare(true)
    if (!phone && !picks.phone) phone = spare(false)
  }

  // Everything any of them held that the result does not, said once each
  // however many records carried it or in whatever case.
  const dropped: string[] = []
  const droppedKeys = new Set<string>()
  const drop = (value: string, key: string) => {
    if (droppedKeys.has(key)) return
    droppedKeys.add(key)
    dropped.push(value)
  }
  for (const person of [kept, ...others]) {
    const personEmail = person.email?.trim()
    if (personEmail && (!email || emailKey(email) !== emailKey(personEmail))) {
      drop(personEmail, `email:${emailKey(personEmail)}`)
    }
    for (const number of [person.mobile?.trim(), person.phone?.trim()]) {
      if (number && !haveNumber(number)) drop(number, `phone:${phoneKey(number)}`)
    }
  }

  return { lastName, position, email, phone, mobile, dropped }
}

/**
 * Every email and number the people being combined hold, once each, for the
 * merge window's choices and the server's check that a pick is one of them.
 */
export function personValues(people: ReadonlyArray<PersonLike>) {
  const emails = new Map<string, string>()
  const numbers = new Map<string, string>()
  for (const person of people) {
    const email = person.email?.trim()
    if (email && !emails.has(emailKey(email))) emails.set(emailKey(email), email)
    for (const number of [person.mobile?.trim(), person.phone?.trim()]) {
      if (number && !numbers.has(phoneKey(number))) numbers.set(phoneKey(number), number)
    }
  }
  return { emails: [...emails.values()], numbers: [...numbers.values()] }
}

/* -------------------------------------------------------------------------- */

const STREET_WORDS: Record<string, string> = {
  street: 'st',
  road: 'rd',
  avenue: 'ave',
  av: 'ave',
  drive: 'dr',
  court: 'ct',
  crescent: 'cres',
  place: 'pl',
  terrace: 'tce',
  parade: 'pde',
  highway: 'hwy',
  boulevard: 'bvd',
  lane: 'ln',
  close: 'cl',
  grove: 'gr',
  circuit: 'cct',
  square: 'sq',
}

export interface AddressLike {
  raw?: string | null
  unit?: string | null
  streetNumber?: string | null
  street?: string | null
  suburb?: string | null
  postcode?: string | null
  placeId?: string | null
}

/**
 * An address as a comparable string: lower case, words, and the street type
 * shortened, so "12 Smith Street, Salisbury" and "12 smith st salisbury" are
 * one place. Built from the parts when there are any, the raw text otherwise.
 */
export function normaliseAddress(address: AddressLike | null | undefined): string | null {
  if (!address) return null
  const parts = [address.unit, address.streetNumber, address.street, address.suburb, address.postcode]
  const text = parts.some((part) => part?.trim()) ? parts.filter(Boolean).join(' ') : (address.raw ?? '')
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter((word) => word !== '' && word !== 'sa' && word !== 'australia')
    .map((word) => STREET_WORDS[word] ?? word)
  return words.length >= 2 ? words.join(' ') : null
}

/**
 * Properties that are the same place: the same Google place, or the same
 * address once normalised. Across the clients being merged this is what the
 * window ticks under Properties — two jobs at one house should be one history.
 */
export function matchSites(sites: ReadonlyArray<{ id: string; address: AddressLike }>): string[][] {
  const pairs: Array<[string, string]> = []
  const firstByKey = new Map<string, string>()
  for (const site of sites) {
    const keys = [
      site.address.placeId ? `place:${site.address.placeId}` : null,
      (() => {
        const text = normaliseAddress(site.address)
        return text ? `text:${text}` : null
      })(),
    ]
    for (const key of keys) {
      if (!key) continue
      const first = firstByKey.get(key)
      if (first) pairs.push([first, site.id])
      else firstByKey.set(key, site.id)
    }
  }
  return groupPairs(pairs)
}

/* -------------------------------------------------------------------------- */

export interface MergeCandidate {
  id: string
  number: number
  xeroContactId?: string | null
  jobCount: number
}

/**
 * Which record a merge should keep, unless somebody says otherwise: the one in
 * Xero, then the one with the most work, then the oldest. The Xero contact is
 * where every invoice already sent lives, so it is the record whose identity
 * matters outside this app.
 */
export function suggestedSurvivor(candidates: ReadonlyArray<MergeCandidate>): string | null {
  const ranked = [...candidates].sort(
    (a, b) =>
      Number(Boolean(b.xeroContactId)) - Number(Boolean(a.xeroContactId)) ||
      b.jobCount - a.jobCount ||
      a.number - b.number,
  )
  return ranked[0]?.id ?? null
}

export type XeroMergeState =
  /** Nobody is in Xero, or the record kept is the one that is. */
  | { kind: 'OK' }
  /** Another record holds the link; keeping this one carries it over. Needs saying yes to. */
  | { kind: 'MOVES'; fromClientId: string; xeroContactId: string }
  /** Two different Xero contacts. Merge them in Xero first. */
  | { kind: 'CONFLICT'; clientIds: string[] }

/** The Xero rule for a merge, in one place for the window and the server. */
export function xeroMergeState(
  candidates: ReadonlyArray<Pick<MergeCandidate, 'id' | 'xeroContactId'>>,
  survivorId: string,
): XeroMergeState {
  const linked = candidates.filter((candidate) => candidate.xeroContactId)
  const distinct = new Set(linked.map((candidate) => candidate.xeroContactId))
  if (distinct.size > 1) return { kind: 'CONFLICT', clientIds: linked.map((candidate) => candidate.id) }

  const holder = linked[0]
  if (!holder || holder.id === survivorId) return { kind: 'OK' }
  return { kind: 'MOVES', fromClientId: holder.id, xeroContactId: holder.xeroContactId! }
}

/* -------------------------------------------------------------------------- */

/** The client fields a merge lets you pick between, in the order the window asks. */
export const MERGE_FIELDS = [
  'name',
  'type',
  'email',
  'phone',
  'billingAddress',
  'taxNumber',
  'website',
  'paymentTermsDays',
  'priceTier',
  'notes',
] as const
export type MergeField = (typeof MERGE_FIELDS)[number]

export const MERGE_FIELD_LABEL: Record<MergeField, string> = {
  name: 'Name',
  type: 'Kind',
  email: 'Email',
  phone: 'Phone',
  billingAddress: 'Billing address',
  taxNumber: 'ABN',
  website: 'Website',
  paymentTermsDays: 'Payment terms',
  priceTier: 'Price tier',
  notes: 'Notes',
}

/**
 * The most clients one merge takes: the kept record and twenty others. A
 * bigger group from the finder is a chain — an agency's numbers linking one
 * landlord to the next — not one customer, so the window asks which of them
 * are the same before it reads them in full.
 */
export const MERGE_MAX_CLIENTS = 21

/** The most clients the window's "which are the same?" list, or Not the same, takes at once. */
export const MERGE_GROUP_LIMIT = 200

export const clientMergeInputSchema = z.object({
  survivorId: z.string(),
  absorbedIds: z
    .array(z.string())
    .min(1)
    .max(MERGE_MAX_CLIENTS - 1),
  /** A name typed in the window, over the one picked in `fields`. */
  name: z.string().trim().min(1).max(200).optional(),
  /**
   * Which record each field is taken from, by client id. A field left out keeps
   * the survivor's value, or the first other record's when the survivor's is
   * empty. Notes may also be `COMBINE`, the default when more than one has some.
   */
  fields: z
    .object({
      name: z.string(),
      type: z.string(),
      email: z.string(),
      phone: z.string(),
      billingAddress: z.string(),
      taxNumber: z.string(),
      website: z.string(),
      paymentTermsDays: z.string(),
      priceTier: z.string(),
      notes: z.string(),
    })
    .partial()
    .default({}),
  /** People to fold together: each list's first id is the one kept. */
  people: z.array(z.array(z.string()).min(2)).default([]),
  /** The email and numbers a combined person keeps, by the kept person's id. */
  peoplePicks: z.record(z.string(), personPicksSchema).default({}),
  /** Properties at one address to fold together: the first id is kept. */
  sites: z.array(z.array(z.string()).min(2)).default([]),
  /** Said yes to the Xero link moving onto the kept record. */
  moveXeroLink: z.boolean().default(false),
})
export type ClientMergeInput = z.infer<typeof clientMergeInputSchema>
